import React, { useEffect, useRef } from 'react';
import { countUpView, formatCount, COUNT_UP_CSS, COUNT_UP_WAVE_PATH, countUpClassName } from '../constants/countUpWidget';
import { ensureBuilderEffectCss } from '../constants/motionEffects';
import { getIconComponent } from './BuilderIconPickerModal';

/**
 * Número que sobe até o valor escolhido. O mesmo desenho vai no HTML publicado.
 */
export default function RenderCountUp({ content = {} }) {
  const valueRef = useRef(null);
  const rootRef = useRef(null);
  const view = countUpView(content);
  const IconCmp = view.icon ? getIconComponent(view.icon) : null;
  const waveDash = view.borderChoice === 'dashed' ? '10 7' : view.borderChoice === 'dotted' ? '2 6' : undefined;

  useEffect(() => {
    ensureBuilderEffectCss();
    const node = valueRef.current;
    const root = rootRef.current;
    if (!node || !root) return undefined;
    let raf = 0;
    let io;
    const paint = (n) => {
      node.textContent = formatCount(n, view.decimals, view.prefix, view.suffix);
    };
    const run = () => {
      if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        paint(view.value);
        return;
      }
      const t0 = performance.now();
      const step = (now) => {
        const p = Math.min(1, (now - t0) / view.duration);
        const eased = 1 - ((1 - p) ** 3);
        paint(view.value * eased);
        if (p < 1) raf = requestAnimationFrame(step);
      };
      raf = requestAnimationFrame(step);
    };
    paint(0);
    if (typeof IntersectionObserver === 'undefined') {
      run();
    } else {
      io = new IntersectionObserver((list) => {
        list.forEach((entry) => {
          if (entry.isIntersecting) {
            io.disconnect();
            run();
          }
        });
      }, { threshold: 0.45 });
      io.observe(root);
    }
    return () => {
      cancelAnimationFrame(raf);
      if (io) io.disconnect();
    };
  }, [view.value, view.decimals, view.prefix, view.suffix, view.duration]);

  const style = {
    '--mc-num': view.numberColor,
    '--mc-text': view.textColor,
    '--mc-card': view.cardBg,
    '--mc-accent': view.accent,
    '--mc-size': `${view.numberSize}px`,
    '--mc-label': `${view.labelSize}px`,
    '--mc-radius': `${view.radius}px`,
    '--mc-border': view.borderColor,
    '--mc-bw': `${view.borderWidth}px`,
    '--mc-bs': view.borderStyle,
    '--mc-organic': view.organicRadius,
    '--mc-icon': view.iconColor,
    '--mc-icon-size': `${view.iconSize}px`,
    '--mc-circle': `${view.circleSize}px`,
  };

  return (
    <>
      <style>{COUNT_UP_CSS}</style>
      <div ref={rootRef} className={countUpClassName(view)} style={style}>
        {view.frame === 'wave' && (
          <svg className="madri-count__blob" viewBox="0 0 300 180" preserveAspectRatio="none" aria-hidden="true">
            <path
              vectorEffect="non-scaling-stroke"
              fill={view.cardBg}
              stroke={view.borderColor}
              strokeWidth={view.borderWidth}
              strokeDasharray={waveDash}
              d={COUNT_UP_WAVE_PATH}
            />
          </svg>
        )}
        {IconCmp ? (
          <div className="madri-count__icon">
            <IconCmp size={view.iconSize} color={view.iconColor} />
          </div>
        ) : null}
        <div ref={valueRef} className="madri-count__value">{formatCount(0, view.decimals, view.prefix, view.suffix)}</div>
        {(view.label || view.caption) && (
          <div className="madri-count__copy">
            {view.label ? <div className="madri-count__label">{view.label}</div> : null}
            {view.caption ? <div className="madri-count__caption">{view.caption}</div> : null}
          </div>
        )}
      </div>
    </>
  );
}
